import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield, Users, DollarSign, TrendingUp, Trash2, RefreshCw,
  Search, Loader2, AlertTriangle, BarChart3, CreditCard, ArrowDown, Activity
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const StatCard = ({ title, value, sub, icon: Icon, color = "text-mystic-300" }) => (
  <Card className="glass" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-mystic-200/50">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {sub && <p className="text-xs text-mystic-200/60 mt-1">{sub}</p>}
        </div>
        <Icon className={`w-8 h-8 ${color} opacity-60`} />
      </div>
    </CardContent>
  </Card>
);

const AdminPanel = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, revenueRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/revenue`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/users?limit=50`, { headers, withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setRevenue(revenueRes.data);
      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      }
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?search=${encodeURIComponent(search)}&limit=50`, { headers, withCredentials: true });
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
    } catch (error) {
      toast.error("Search failed");
    }
  };

  const viewUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers, withCredentials: true });
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm("Are you sure? This permanently deletes the user and ALL their data.")) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers, withCredentials: true });
      toast.success("User deleted");
      setSelectedUser(null);
      setUserDetail(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  const processRefund = async (transactionId) => {
    if (!confirm("Process refund? This will refund the payment and downgrade the user to free.")) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/refund`, {
        transaction_id: transactionId, reason: refundReason || "Admin refund"
      }, { headers, withCredentials: true });
      toast.success(`Refund ${res.data.status}: $${res.data.amount}`);
      if (selectedUser) viewUserDetail(selectedUser);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Refund failed");
    } finally {
      setActionLoading(false);
      setRefundReason("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6 text-center" data-testid="admin-denied">
        <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl text-white mb-2">Admin Access Required</h2>
        <p className="text-mystic-200/70">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="admin-panel">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">
            <Shield className="w-8 h-8 inline mr-2 text-amber-400" />
            Admin Panel
          </h1>
          <p className="text-mystic-200/60 text-sm">Manage users, view statistics, and process refunds</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href="/admin/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-mystic-600 to-mystic-500 text-white text-sm font-medium hover:from-mystic-500 hover:to-mystic-400 transition-all shadow-lg"
            data-testid="open-admin-dashboard-link"
          >
            <BarChart3 className="w-4 h-4" />
            Open Live Dashboard
          </a>
          <a
            href="/admin/system"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-700 to-emerald-600 text-white text-sm font-medium hover:from-emerald-600 hover:to-emerald-500 transition-all shadow-lg"
            data-testid="open-admin-system-link"
          >
            <Activity className="w-4 h-4" />
            System Health
          </a>
        </div>
      </motion.div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="glass w-full mb-6">
          <TabsTrigger value="overview" className="flex-1" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" className="flex-1" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="revenue" className="flex-1" data-testid="tab-revenue">Revenue</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Total Users" value={stats.users.total} sub={`+${stats.users.new_this_week} this week`} icon={Users} />
            <StatCard title="Premium Users" value={stats.subscriptions.total_premium} sub={`${stats.subscriptions.conversion_rate}% conversion`} icon={CreditCard} color="text-amber-400" />
            <StatCard title="Active (Week)" value={stats.users.active_this_week} sub={`${stats.users.active_this_month} this month`} icon={TrendingUp} color="text-green-400" />
            <StatCard title="Net Revenue" value={`$${revenue?.summary?.net_revenue || 0}`} sub={`$${revenue?.summary?.monthly || 0} this month`} icon={DollarSign} color="text-emerald-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="glass">
              <CardHeader><CardTitle className="text-lg text-white">Subscriptions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-mystic-200/70">Monthly Premium</span><span className="text-white font-medium">{stats.subscriptions.premium_monthly}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Yearly Premium</span><span className="text-white font-medium">{stats.subscriptions.premium_yearly}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Cancelled</span><span className="text-red-400 font-medium">{stats.subscriptions.cancelled}</span></div>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardHeader><CardTitle className="text-lg text-white">Content Stats</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-mystic-200/70">Cycle Logs</span><span className="text-white font-medium">{stats.content.cycle_logs}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Journal Entries</span><span className="text-white font-medium">{stats.content.journal_entries}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Meditation Sessions</span><span className="text-white font-medium">{stats.content.meditation_sessions}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Forum Posts</span><span className="text-white font-medium">{stats.content.forum_posts}</span></div>
              </CardContent>
            </Card>
          </div>

          <Card className="glass">
            <CardHeader><CardTitle className="text-lg text-white">Feature Adoption</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center"><p className="text-2xl font-bold text-white">{stats.features.birth_charts}</p><p className="text-xs text-mystic-200/60">Birth Charts</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{stats.features.numerology_profiles}</p><p className="text-xs text-mystic-200/60">Numerology</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{stats.features.push_subscribers}</p><p className="text-xs text-mystic-200/60">Push Subscribers</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{stats.features.digest_subscribers}</p><p className="text-xs text-mystic-200/60">Digest Subscribers</p></div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex gap-3">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or user ID..." className="bg-mystic-800/50 border-mystic-600 text-white flex-1" onKeyDown={(e) => e.key === "Enter" && searchUsers()} data-testid="user-search-input" />
            <Button onClick={searchUsers} className="bg-mystic-600 hover:bg-mystic-500 rounded-full" data-testid="user-search-btn"><Search className="w-4 h-4 mr-1" /> Search</Button>
            <Button onClick={() => { setSearch(""); fetchData(); }} variant="outline" className="border-mystic-600 text-mystic-200 rounded-full"><RefreshCw className="w-4 h-4" /></Button>
          </div>

          <p className="text-mystic-200/50 text-sm">{totalUsers} total users</p>

          {/* User Detail Panel */}
          {selectedUser && userDetail && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="glass border-amber-500/20" data-testid="user-detail-panel">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-white">{userDetail.user?.name} — {userDetail.user?.email}</CardTitle>
                  <Button variant="ghost" size="sm" className="text-mystic-200" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>Close</Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-mystic-200/50">User ID:</span> <span className="text-white">{userDetail.user?.user_id}</span></div>
                    <div><span className="text-mystic-200/50">Subscription:</span> <Badge className={userDetail.subscription?.tier?.includes("premium") ? "bg-amber-500/20 text-amber-300" : "bg-gray-500/20 text-gray-300"}>{userDetail.subscription?.tier || "free"}</Badge></div>
                    <div><span className="text-mystic-200/50">Created:</span> <span className="text-white">{userDetail.user?.created_at?.split("T")[0]}</span></div>
                    <div><span className="text-mystic-200/50">Apple ID:</span> <span className="text-white">{userDetail.user?.apple_id ? "Yes" : "No"}</span></div>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-7 gap-2 text-center text-xs">
                    {Object.entries(userDetail.data_counts || {}).map(([key, val]) => (
                      <div key={key} className="glass-light rounded-lg p-2"><p className="text-white font-bold">{val}</p><p className="text-mystic-200/50">{key.replace(/_/g, " ")}</p></div>
                    ))}
                  </div>
                  {/* Payment History */}
                  {userDetail.payment_history?.length > 0 && (
                    <div>
                      <p className="text-sm text-mystic-200/70 mb-2">Payment History</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {userDetail.payment_history.map((txn) => (
                          <div key={txn.transaction_id} className="flex items-center justify-between glass-light rounded-lg p-2 text-sm">
                            <div>
                              <span className="text-white">${txn.amount}</span>
                              <span className="text-mystic-200/50 ml-2">{txn.tier_id}</span>
                              <Badge className={`ml-2 text-xs ${txn.status === "completed" ? "bg-green-500/20 text-green-300" : txn.status === "refunded" ? "bg-red-500/20 text-red-300" : "bg-yellow-500/20 text-yellow-300"}`}>{txn.status}</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-mystic-200/40 text-xs">{txn.created_at?.split("T")[0]}</span>
                              {txn.status === "completed" && (
                                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 text-xs" onClick={() => processRefund(txn.transaction_id)} disabled={actionLoading} data-testid={`refund-btn-${txn.transaction_id}`}>
                                  <ArrowDown className="w-3 h-3 mr-1" /> Refund
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Refund reason (optional)" className="bg-mystic-800/50 border-mystic-600 text-white mt-2 text-sm" data-testid="refund-reason-input" />
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => deleteUser(selectedUser)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full" disabled={actionLoading} data-testid="delete-user-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete User & Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* User List */}
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} className={`flex items-center justify-between glass-light rounded-lg p-3 cursor-pointer hover:bg-white/5 transition ${selectedUser === u.user_id ? "border border-amber-500/30" : ""}`} onClick={() => viewUserDetail(u.user_id)} data-testid={`user-row-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    {u.is_admin && <Badge className="bg-amber-500/20 text-amber-300 text-xs">Admin</Badge>}
                  </div>
                  <p className="text-mystic-200/50 text-xs truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <Badge className={u.subscription?.includes("premium") ? "bg-purple-500/20 text-purple-300" : "bg-gray-500/20 text-gray-400"}>{u.subscription}</Badge>
                  <span className="text-mystic-200/40">{u.cycle_logs_count} logs</span>
                  <span className="text-mystic-200/40">{u.created_at?.split("T")[0]}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* REVENUE TAB */}
        <TabsContent value="revenue" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard title="Weekly Revenue" value={`$${revenue?.summary?.weekly || 0}`} icon={DollarSign} color="text-green-400" />
            <StatCard title="Monthly Revenue" value={`$${revenue?.summary?.monthly || 0}`} icon={DollarSign} color="text-emerald-400" />
            <StatCard title="Yearly Revenue" value={`$${revenue?.summary?.yearly || 0}`} icon={DollarSign} color="text-teal-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="glass">
              <CardHeader><CardTitle className="text-lg text-white">Revenue by Plan</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-mystic-200/70">Monthly Premium ($11.33/mo)</span><span className="text-white font-medium">${revenue?.by_plan?.premium_monthly || 0}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Yearly Premium ($91.09/yr)</span><span className="text-white font-medium">${revenue?.by_plan?.premium_yearly || 0}</span></div>
                <div className="flex justify-between border-t border-mystic-600/30 pt-2"><span className="text-mystic-200/70">Total Revenue</span><span className="text-emerald-400 font-bold">${revenue?.summary?.total || 0}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">Refunds</span><span className="text-red-400">-${revenue?.summary?.total_refunds || 0}</span></div>
                <div className="flex justify-between border-t border-mystic-600/30 pt-2"><span className="text-white font-medium">Net Revenue</span><span className="text-emerald-400 font-bold">${revenue?.summary?.net_revenue || 0}</span></div>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardHeader><CardTitle className="text-lg text-white">Revenue by Provider</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-mystic-200/70">Stripe</span><span className="text-white font-medium">${revenue?.by_provider?.stripe || 0}</span></div>
                <div className="flex justify-between"><span className="text-mystic-200/70">PayPal</span><span className="text-white font-medium">${revenue?.by_provider?.paypal || 0}</span></div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend */}
          <Card className="glass" data-testid="monthly-trend">
            <CardHeader><CardTitle className="text-lg text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-mystic-300" /> Monthly Revenue Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {revenue?.monthly_trend?.filter(m => m.revenue > 0 || m.transactions > 0).length === 0 ? (
                  <p className="text-mystic-200/50 text-sm text-center py-4">No revenue data yet. Revenue will appear here after first payments.</p>
                ) : (
                  revenue?.monthly_trend?.map((m, idx) => (
                    <div key={`trend-${idx}-${m.month}`} className="flex items-center gap-3">
                      <span className="text-mystic-200/60 text-xs w-28 shrink-0">{m.month}</span>
                      <div className="flex-1 bg-mystic-800/30 rounded-full h-6 overflow-hidden">
                        <div className="bg-gradient-to-r from-emerald-600 to-emerald-400 h-full rounded-full flex items-center px-2" style={{ width: `${Math.max(m.revenue / Math.max(...revenue.monthly_trend.map(x => x.revenue), 1) * 100, m.revenue > 0 ? 10 : 0)}%` }}>
                          {m.revenue > 0 && <span className="text-xs text-white font-medium">${m.revenue}</span>}
                        </div>
                      </div>
                      <span className="text-mystic-200/40 text-xs w-12 text-right">{m.transactions} txn</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          {revenue?.recent_transactions?.length > 0 && (
            <Card className="glass">
              <CardHeader><CardTitle className="text-lg text-white">Recent Transactions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {revenue.recent_transactions.map((txn) => (
                    <div key={txn.transaction_id} className="flex items-center justify-between glass-light rounded-lg p-3 text-sm">
                      <div>
                        <span className="text-white font-medium">${txn.amount}</span>
                        <span className="text-mystic-200/50 ml-2">{txn.tier_id}</span>
                        <span className="text-mystic-200/40 ml-2">{txn.user_id?.slice(0, 16)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${txn.status === "completed" ? "bg-green-500/20 text-green-300" : txn.status === "refunded" ? "bg-red-500/20 text-red-300" : "bg-yellow-500/20 text-yellow-300"}`}>{txn.status}</Badge>
                        <span className="text-mystic-200/40 text-xs">{txn.created_at?.split("T")[0]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
